// MarkdownToolbar — three buttons that insert the constrained markdown the
// mini renderer (utils/miniMarkdown) understands: **bold** wraps the current
// selection; the bulleted / numbered buttons prefix each selected line with
// "- " / "N. ". It mutates the bound <textarea> through a ref so the caret and
// selection are preserved, then reports the new value via onChange. Stored as
// plain markdown text — description-only for now (comments stay plain text).

import { Bold, List, ListOrdered } from "lucide-react";
import type { RefObject } from "react";

export interface MarkdownToolbarProps {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}

function wrapSelection(
  ta: HTMLTextAreaElement,
  value: string,
  onChange: (next: string) => void,
  marker: string,
): void {
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${marker}${selected}${marker}${value.slice(end)}`;
  onChange(next);
  requestAnimationFrame(() => {
    ta.focus();
    // Keep the original text selected, now sitting inside the markers.
    ta.setSelectionRange(start + marker.length, end + marker.length);
  });
}

function prefixLines(
  ta: HTMLTextAreaElement,
  value: string,
  onChange: (next: string) => void,
  makePrefix: (lineIndex: number) => string,
): void {
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  // Expand the selection to whole lines so a prefix lands at each line start.
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  const nextNewline = value.indexOf("\n", end);
  const blockEnd = nextNewline === -1 ? value.length : nextNewline;
  const block = value.slice(lineStart, blockEnd);
  const prefixed = block
    .split("\n")
    .map((line, idx) => `${makePrefix(idx)}${line}`)
    .join("\n");
  const next = `${value.slice(0, lineStart)}${prefixed}${value.slice(blockEnd)}`;
  onChange(next);
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(lineStart, lineStart + prefixed.length);
  });
}

const BTN_CLASS =
  "inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200";

export default function MarkdownToolbar({
  textareaRef,
  value,
  onChange,
}: MarkdownToolbarProps) {
  return (
    <div
      data-testid="markdown-toolbar"
      className="flex items-center gap-0.5 rounded-t-md border border-b-0 border-slate-200 bg-slate-50 px-1 py-0.5 dark:border-slate-700 dark:bg-slate-800/60"
    >
      <button
        type="button"
        aria-label="Bold"
        title="Bold"
        className={BTN_CLASS}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const ta = textareaRef.current;
          if (ta) wrapSelection(ta, value, onChange, "**");
        }}
      >
        <Bold aria-hidden="true" className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Bulleted list"
        title="Bulleted list"
        className={BTN_CLASS}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const ta = textareaRef.current;
          if (ta) prefixLines(ta, value, onChange, () => "- ");
        }}
      >
        <List aria-hidden="true" className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Numbered list"
        title="Numbered list"
        className={BTN_CLASS}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const ta = textareaRef.current;
          if (ta) prefixLines(ta, value, onChange, (idx) => `${idx + 1}. `);
        }}
      >
        <ListOrdered aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
